import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
} from '@opentelemetry/sdk-trace-base';
import type { SpanProcessor, Span } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  MeterProvider,
  PeriodicExportingMetricReader,
} from '@opentelemetry/sdk-metrics';
import { metrics, type Counter } from '@opentelemetry/api';
import { logs } from '@opentelemetry/api-logs';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import {
  LoggerProvider,
  BatchLogRecordProcessor,
} from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { CONFIG } from 'config/environment_variables';
import { INSTRUMENTATION } from 'constant/instrumentation';
import { getServerName } from 'utils/functions/server';
import { getOSInfo, getBrowserInfo } from 'utils/platform';
import { getNormalizedPathname } from 'utils/functions/window';
import { ServerName } from 'types/config';
import {
  startLongTaskMonitor,
  startGlobalErrorMonitor,
} from 'utils/observability/monitor';
import {
  getLiveContext,
  initLiveContext,
} from 'utils/observability/liveContext';
import { OTEL_INGEST_PATH } from 'utils/observability/otelIngest';
import {
  isInternalUser,
  parseSampleRatio,
} from 'utils/observability/tracingGate';
import { parseOtlpHeaders } from 'utils/observability/otelHeaders';

// Single OTLP egress endpoint for ALL signals — logs, metrics, and traces. Points
// at the browser-facing OTel Collector (which injects the Honeycomb key
// server-side) or, interim, directly at https://api.honeycomb.io. Empty ⇒ all
// telemetry stays off.
const otelBase = CONFIG.OTEL_COLLECTOR_URL?.replace(/\/+$/, '');

// Auth headers applied to every OTLP exporter. Empty in the collector model (the
// browser posts unauthenticated; the collector injects the key). Populated only
// for the interim direct-to-Honeycomb path — set
// VITE_APP_OTEL_HEADERS=x-honeycomb-team=<key>,x-honeycomb-dataset=<name> (the
// dataset is required by metrics and ignored by traces/logs, which route on
// service.name) and point _COLLECTOR_URL at https://api.honeycomb.io. Inlined into
// the public bundle, so treat any key here as public and rotate on collector cutover.
const otelHeaders = parseOtlpHeaders(CONFIG.OTEL_HEADERS);

// Path-based ignore for the exporters' OWN telemetry POSTs — breaks the exporter
// self-loop that caused the span storm. See utils/observability/otelIngest.ts for
// why it matches the PATH (immune to :443 normalisation) rather than the full URL.

// Only inject the W3C traceparent header into our own backend — never third
// parties (leaks internal trace context and triggers CORS preflight failures).
const escapeRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const backendUrlPatterns = CONFIG.API_URL
  ? [new RegExp(`^${escapeRegex(CONFIG.API_URL)}`)]
  : [];

// Computed once — os.name does not change during a session.
const osName = getOSInfo();

// --- Gating ----------------------------------------------------------------
// A collector is a HARD requirement (nowhere to send otherwise). Beyond that,
// tracing turns on when EITHER the env flag promotes it to everyone OR the
// signed-in user is internal (Varicon staff) — internal users get early access
// for dogfooding, and GA is then a config-only flip of the flag (no code change).
//
// The internal check is a ROLLOUT lever, not an architectural boundary — init
// still happens at startup so we keep the ability to instrument unauthenticated
// flows (login funnel, client portals, guest QR) in a later phase. It reads from
// sessionStorage (not the React auth context) because instrumentation initialises
// before the app mounts; on a fresh login tracing starts on the next navigation.
//
// NB: with this OR, the flag is not a hard kill switch — once a collector is
// configured in an env, internal users trace there regardless of the flag. To
// force everything off in an env, leave OTEL_COLLECTOR_URL unset.
//
// isInternalUser / parseSampleRatio live in utils/observability/tracingGate so
// they are unit-testable — importing this module boots the SDK as a side effect.
const tracingEnabled =
  !!otelBase && (CONFIG.ENABLE_TRACING === 'true' || isInternalUser());

const sampleRatio = parseSampleRatio(CONFIG.OTEL_TRACE_SAMPLE_RATIO);

// Volume signal (see decision "make drops loud"). BatchSpanProcessor drops
// silently once its queue overflows and exposes no drop hook, so we count spans
// CREATED here; drops surface as (spans_started − spans received in Honeycomb),
// and a spike flags interaction fan-out before it becomes a storm.
let spanStartedCounter: Counter | undefined;
function recordSpanStarted(): void {
  spanStartedCounter?.add(1);
}

// ---------------------------------------------------------------------------
// Dynamic attribute span processor
//
// Stamps per-session identity + environment on every span. All values come from
// getLiveContext() (a cache refreshed off the hot path) or cheap live reads, so
// onStart does NO storage I/O and NO heap computation — unlike the pre-disable
// version, whose per-span localStorage + getHeapSize() calls fed main-thread jank.
//
// PII: only opaque identifiers are stamped (user.id / tenant.id are UUIDs that
// join back to identity in the DB). user.email and tenant.name are deliberately
// NOT emitted — content stays out of telemetry at the source. screen.name uses
// the NORMALISED path (/projects/:id), which also drops record ids and any query
// string. This is the primary PII control; the collector redaction is a backstop.
// ---------------------------------------------------------------------------
class DynamicAttributeSpanProcessor implements SpanProcessor {
  onStart(span: Span): void {
    recordSpanStarted();
    const ctx = getLiveContext();
    span.setAttribute('user.id', ctx.userId);
    span.setAttribute('tenant.id', ctx.tenantId);
    span.setAttribute('os.name', osName);
    span.setAttribute('screen.name', getNormalizedPathname());
    span.setAttribute('memory.jsHeapSizeLimit', ctx.heap.jsHeapSizeLimit);
    span.setAttribute('memory.totalJSHeapSize', ctx.heap.totalJSHeapSize);
    span.setAttribute('memory.usedJSHeapSize', ctx.heap.usedJSHeapSize);
  }

  onEnd(): void { }

  forceFlush(): Promise<void> {
    return Promise.resolve();
  }

  shutdown(): Promise<void> {
    return Promise.resolve();
  }
}

// Resource holds stable, service-level attributes for ALL telemetry from this
// browser session, set once at init. user/tenant identity is intentionally
// omitted here (frozen-at-load ⇒ always 'Unknown' pre-login); it is stamped live
// per-span by DynamicAttributeSpanProcessor and per-log by getLiveUserAttrs().
const resource = resourceFromAttributes({
  'service.name': INSTRUMENTATION.SERVICE_NAME,
  'app.build_version': __APP_BUILD_VERSION__,
  'deployment.environment': getServerName() as ServerName,
  'browser.name': getBrowserInfo(),
  'browser.user_agent': navigator.userAgent,
  'os.name': getOSInfo(),
  'browser.language': navigator.language,
  'device.type': /Mobile|Android|iPhone/i.test(navigator.userAgent)
    ? 'mobile'
    : 'desktop',
  'screen.width': window?.screen?.width ?? '0',
  'screen.height': window?.screen?.height ?? '0',
  'viewport.width': window?.innerWidth ?? '0',
  'viewport.height': window?.innerHeight ?? '0',
  'browser.version': navigator.appVersion,
});

// --- Logs + metrics (egress to the OTLP endpoint: collector / Honeycomb) -----
const loggerProvider = new LoggerProvider({
  resource: resource,
  processors: [
    new BatchLogRecordProcessor({
      exporter: new OTLPLogExporter({
        url: `${otelBase}/v1/logs`,
        headers: otelHeaders,
      }),
      // Default is 1000ms — causes a /v1/logs HTTP call every second when any log is queued.
      // 30s matches the trace processor interval; logs are low-frequency so data loss risk is minimal.
      // scheduledDelayMillis: 30000,
      maxExportBatchSize: 512,
      maxQueueSize: 2048,
    }),
  ],
});

const metricReader = new PeriodicExportingMetricReader({
  exporter: new OTLPMetricExporter({
    url: `${otelBase}/v1/metrics`,
    headers: otelHeaders,
  }),
  exportIntervalMillis: 5 * 60_000, // Export metrics every 5 minutes (default is 10 seconds)
});

const myServiceMeterProvider = new MeterProvider({
  resource,
  readers: [metricReader],
});

// Guard against double-registration in React Strict Mode / HMR — otherwise each
// hot-reload re-runs the setGlobal* calls and re-registers processors, leaving
// stale export timers stacking up on every save.
if (!window.__otelInitialized && otelBase) {
  window.__otelInitialized = true;
  metrics.setGlobalMeterProvider(myServiceMeterProvider);
  logs.setGlobalLoggerProvider(loggerProvider);

  startLongTaskMonitor();
  startGlobalErrorMonitor();

  // Metrics have no auto-flush; the browser log/span batch processors DO
  // auto-flush on visibilitychange/pagehide (sdk-logs/sdk-trace-web ≥ 0.220/2.x),
  // so only metrics need a manual tab-hide flush.
  //
  // The old per-`error` / per-`unhandledrejection` forceFlush handlers were
  // REMOVED on purpose: they turned every unhandled rejection (e.g. React Query
  // AbortController cancellations) into an immediate burst of export POSTs — an
  // amplifier that contributed to the telemetry storm.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      myServiceMeterProvider.forceFlush();
    }
  });

  // --- Traces (gated) -------------------------------------------------------
  if (tracingEnabled) {
    initLiveContext();

    const traceProvider = new WebTracerProvider({
      resource,
      // Head-sample at the SOURCE to bound browser CPU + bandwidth regardless of
      // interaction fan-out. Parent-based so the browser's keep/drop decision
      // propagates coherently into the backend spans (no half-sampled traces).
      sampler: new ParentBasedSampler({
        root: new TraceIdRatioBasedSampler(sampleRatio),
      }),
      spanProcessors: [
        // Must precede BatchSpanProcessor so attributes are stamped before enqueue.
        new DynamicAttributeSpanProcessor(),
        new BatchSpanProcessor(
          new OTLPTraceExporter({
            url: `${otelBase}/v1/traces`,
            // Empty in the collector model (unauthenticated POST; collector adds
            // the key). Populated only for the interim direct-to-Honeycomb path —
            // see `otelHeaders` above and otelHeaders.ts.
            headers: otelHeaders,
          }),
          {
            scheduledDelayMillis: 30000,
            maxExportBatchSize: 512,
            maxQueueSize: 2048, // NB: overflow is dropped silently — see spans_started counter.
          },
        ),
      ],
    });

    traceProvider.register({
      contextManager: new ZoneContextManager(),
    });

    spanStartedCounter = metrics
      .getMeter('frontend-tracing')
      .createCounter('otel.frontend.spans_started', {
        description:
          'Browser spans created — volume signal; drops = started − received in Honeycomb.',
      });

    registerInstrumentations({
      instrumentations: [
        new FetchInstrumentation({
          ignoreUrls: [OTEL_INGEST_PATH],
          propagateTraceHeaderCorsUrls: backendUrlPatterns,
          // Suppress DNS/TCP/TLS sub-events to keep payloads lean.
          ignoreNetworkEvents: true,
        }),
        new UserInteractionInstrumentation({
          // 'input' deliberately omitted — one span per keystroke is high-volume,
          // low-value, and a fan-out risk.
          eventNames: ['click', 'submit'],
        }),
        new XMLHttpRequestInstrumentation({
          ignoreUrls: [OTEL_INGEST_PATH],
          propagateTraceHeaderCorsUrls: backendUrlPatterns,
        }),
        new DocumentLoadInstrumentation(),
      ],
    });
  }
}
